import {
  HomeOutlined,
  PictureOutlined,
  ReadOutlined,
  ShopOutlined,
  TagOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useOidc, useOidcAccessToken, useOidcUser } from '@axa-fr/react-oidc';
import { Alert, theme } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { BiSolidPackage } from 'react-icons/bi';
import { TbRepeat } from 'react-icons/tb';
import { Link, Outlet } from 'react-router';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useGetMarketplaces } from '../../utils/api/marketplaces/useGetMarketplaces';
import { getMarketplaceIcon } from '../../utils/const/mp';
import { useProfile } from '../../utils/zustand/hooks/useProfile';
import DashboardBreadcrumbs from './breadcrumbs';
import DocumentationDrawer from './documentation';
import DashboardHeader from './header';
import HistoryDrawer from './history';
import NotificationDrawer from './notifications/NotificationDrawer';
import {
  StyledContent,
  StyledInnerLayout,
  StyledLayout,
  StyledMenu,
  StyledSider,
} from './styled';

// TODO: handle trad
const getMenuItems = (marketplaces, permissions) => [
  {
    key: 'dashboard',
    disabled: true,
    label: (
      <Link to="/dashboard/home" disabled>
        Dashboard
      </Link>
    ),
    icon: <HomeOutlined />,
  },
  {
    key: 'products',
    label: <Link to="/dashboard/products">Catalogue</Link>,
    icon: <ReadOutlined />,
    children: [
      {
        key: 'raw_products',
        label: <Link to="/dashboard/products">Produits bruts</Link>,
        icon: <BiSolidPackage />,
      },
      {
        key: 'marketplace_products',
        label: <Link to="/dashboard/mp-products">Produits marketplace</Link>,
        icon: <BiSolidPackage />,
      },
      {
        key: 'offers',
        label: <Link to="/dashboard/mp-offers">Offres marketplace</Link>,
        icon: <BiSolidPackage />,
      },
    ],
  },
  {
    key: 'marketplaces',
    label: <Link to="/dashboard/marketplaces">Marketplaces</Link>,
    icon: <ShopOutlined />,
    children: marketplaces?.map((mp) => ({
      key: mp.name,
      label: <Link to={`/dashboard/marketplaces/${mp.name}`}>{mp.title}</Link>,
      icon: (
        <img
          src={getMarketplaceIcon(mp.name)}
          alt={mp.name}
          style={{ width: 24, height: 24 }}
        />
      ),
      children: [
        {
          key: `${mp.name}_categories`,
          label: (
            <Link to={`/dashboard/marketplaces/${mp.name}/categories`}>
              Catégories
            </Link>
          ),
          icon: <TagOutlined />,
        },
      ],
    })),
  },
  {
    key: 'categories',
    label: <Link to="/dashboard/categories">Catégories brutes</Link>,
    icon: <TagOutlined />,
  },
  {
    key: 'mapping',
    label: <Link to="/dashboard/mapping">Mapping</Link>,
    icon: <TbRepeat />,
  },
  {
    key: 'medias',
    label: <Link to="/dashboard/medias">Médias</Link>,
    icon: <PictureOutlined />,
  },
  ...(permissions?.isPrometheeAdmin
    ? [
        {
          key: 'admin',
          label: <Link to="/dashboard/admin/users">Administration</Link>,
          icon: <TeamOutlined />,
          children: [
            {
              key: 'admin_users',
              label: <Link to="/dashboard/admin/users">Utilisateurs</Link>,
              icon: <TeamOutlined />,
            },
          ],
        },
      ]
    : []),
];

const Dashboard = () => {
  const [openHistory, setOpenHistory] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [openDocumentation, setOpenDocumentation] = useState(false);
  const [collapsedMenu, setCollapsedMenu] = useState(false);
  const { profile, setProfile } = useProfile();
  const { accessToken } = useOidcAccessToken();
  const { oidcUser } = useOidcUser();
  const { oidcClient } = useOidc();

  const { marketplaces } = useGetMarketplaces({ is_active: true });
  const permissions = useUserPermissions();

  // Utilisateur authentifié via SSO mais sans aucun droit Prométhée
  const hasNoPrometheeAccess =
    permissions.isAuthenticated &&
    !permissions.isSuperuser &&
    permissions.groups.length === 0;

  const menuItems = useMemo(
    () => getMenuItems(marketplaces, permissions),
    [marketplaces, permissions]
  );

  useEffect(() => {
    if (!profile && !!oidcUser) {
      console.log('profile set:', oidcUser?.name);
      setProfile(oidcUser);
    }
  }, [accessToken, oidcUser, oidcClient, profile, setProfile]);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleHistoryDrawer = () => {
    if (openNotification) {
      setOpenNotification(false);
    }
    if (openDocumentation) {
      setOpenDocumentation(false);
    }
    setOpenHistory((open) => !open);
  };

  const handleDocumentationDrawer = () => {
    if (openHistory) {
      setOpenHistory(false);
    }
    if (openNotification) {
      setOpenNotification(false);
    }
    setOpenDocumentation((open) => !open);
  };

  const handleNotificationDrawer = () => {
    if (openHistory) {
      setOpenHistory(false);
    }
    if (openDocumentation) {
      setOpenDocumentation(false);
    }
    setOpenNotification((open) => !open);
  };

  return (
    <StyledLayout>
      <DashboardHeader
        profile={profile}
        handleHistoryDrawer={handleHistoryDrawer}
        handleNotificationDrawer={handleNotificationDrawer}
        handleDocumentationDrawer={handleDocumentationDrawer}
        onCollapse={() => setCollapsedMenu((s) => !s)}
      />
      <StyledLayout>
        <StyledSider
          width={220}
          background={colorBgContainer}
          collapsible
          collapsed={collapsedMenu}
          onCollapse={() => setCollapsedMenu((s) => !s)}
        >
          <StyledMenu
            mode="inline"
            defaultOpenKeys={['products']}
            items={menuItems}
          />
        </StyledSider>
        <StyledInnerLayout>
          <DashboardBreadcrumbs />
          <StyledContent
            background={colorBgContainer}
            $borderRadius={borderRadiusLG}
          >
            <HistoryDrawer open={openHistory} setOpen={setOpenHistory} />
            <NotificationDrawer
              open={openNotification}
              setOpen={setOpenNotification}
            />
            <DocumentationDrawer
              open={openDocumentation}
              setOpen={setOpenDocumentation}
            />
            {hasNoPrometheeAccess && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Aucun droit Prométhée ne t'est attribué"
                description="Ton compte est bien connecté, mais aucun rôle ni permission Prométhée ne t'a encore été accordé. Contacte un administrateur pour obtenir un accès (les pages afficheront sinon des erreurs d'autorisation)."
              />
            )}
            <Outlet />
          </StyledContent>
        </StyledInnerLayout>
      </StyledLayout>
    </StyledLayout>
  );
};
export default Dashboard;
